import { DIALOG_DATA } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Injector,
  InjectionToken,
  Inject,
  signal,
  Type
} from '@angular/core';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { featherXCircle } from '@ng-icons/feather-icons';

import { ModalService } from './modal.service';

export interface ModalConfig {
  title?: string;
  description?: string;
  closeButton?: boolean | { showOnMobile?: boolean; showOnDesktop?: boolean };
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  enableFullscreenOnMobile?: boolean;
  backgroundColor?: string;
}

interface ModalComponentData<T> {
  component: Type<T>;
  options?: ModalConfig;
}

export type ModalData<T> = {
  [P in keyof T]: T[P];
};

export const MODAL_DATA = new InjectionToken<ModalData<unknown>>('MODAL_DATA');

@Component({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ui-modal',
  imports: [CommonModule, NgIconComponent],
  providers: [
    provideIcons({
      featherXCircle
    })
  ],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss']
})
export class ModalComponent<T> {
  protected component = signal<Type<T> | null>(null);

  protected config = signal<ModalConfig>({
    closeButton: false,
    enableFullscreenOnMobile: false,
    rounded: 'md',
    size: 'md'
  });

  protected dynamicComponentInjector: Injector | undefined;
  readonly #injector = inject(Injector);

  constructor(
    private modal: ModalService,
    @Inject(DIALOG_DATA) data: ModalComponentData<T>
  ) {
    const { component, options, ...componentInputs } = data || {};

    this.component.set(component);
    this.config.update((prev) => ({ ...prev, ...options }));

    this.dynamicComponentInjector = Injector.create({
      providers: [{ provide: MODAL_DATA, useValue: componentInputs }],
      name: 'MODAL_DATA',
      parent: this.#injector
    });
  }

  readonly hasTitle = computed(() => !!this.config().title);

  readonly hasDescription = computed(() => !!this.config().description);

  readonly isFullscreenOnMobile = computed(
    () => this.config().enableFullscreenOnMobile ?? false
  );

  readonly size = computed(() => {
    const size = this.config().size;
    return size ?? 'md';
  });

  readonly rounded = computed(() => {
    const rounded = this.config().rounded;
    return rounded ?? 'md';
  });

  readonly backgroundColor = computed(() => {
    const color = this.config().backgroundColor;
    return color || '';
  });

  readonly shouldShowCloseButtonOnMobile = computed(() => {
    const close = this.config().closeButton;
    return typeof close === 'object' ? !!close.showOnMobile : !!close;
  });

  readonly shouldShowCloseButtonOnDesktop = computed(() => {
    const close = this.config().closeButton;
    return typeof close === 'object' ? !!close.showOnDesktop : !!close;
  });

  readonly shouldShowCloseButton = computed(
    () =>
      this.shouldShowCloseButtonOnMobile() ||
      this.shouldShowCloseButtonOnDesktop()
  );

  readonly shouldShowTopBar = computed(
    () => this.hasTitle() || this.shouldShowCloseButton()
  );

  readonly closeButtonVisibilityClass = computed(() => {
    const showOnMobile = this.shouldShowCloseButtonOnMobile();
    const showOnDesktop = this.shouldShowCloseButtonOnDesktop();

    if (showOnMobile && showOnDesktop) {
      return '';
    }

    if (!showOnMobile && showOnDesktop) {
      return 'pointer-events-none hidden sm:pointer-events-auto sm:flex';
    }

    return 'pointer-events-auto flex sm:pointer-events-none sm:hidden';
  });

  onClose(): void {
    this.modal.close();
  }
}
